import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  PdsModal,
  PdsModalHeader,
  PdsModalBody,
  PdsModalFooter,
  PdsBox,
  PdsMantineText,
  PdsMantineButton,
  PdsCheckbox,
  PdsCard,
  PdsSpinnerIcon,
} from '@podium-design-system/react-components';
import {
  EXPORT_TOOLS,
  buildDefaultSectionSelection,
  selectedSectionIds,
  setToolSections,
  toolSelectionState,
} from '../utils/investigationExportCatalog';

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'argus-export';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function filenameFromContentDisposition(header) {
  if (!header) return null;
  const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1]);
    } catch {
      return utfMatch[1];
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] || null;
}

/**
 * Investigation export dialog: nested tool/section checkboxes → backend CSV/XLSX.
 */
export default function InvestigationExportModal({
  isOpen,
  onClose,
  investigationId,
  targetDomain,
  onError,
}) {
  const [selection, setSelection] = useState(() => buildDefaultSectionSelection());
  const [format, setFormat] = useState('xlsx');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelection(buildDefaultSectionSelection());
      setFormat('xlsx');
      setLoading(false);
    }
  }, [isOpen]);

  const selectedIds = useMemo(() => selectedSectionIds(selection), [selection]);
  const hasSelection = selectedIds.length > 0;

  if (!isOpen) return null;

  const handleSelectAll = () => setSelection(buildDefaultSectionSelection());
  const handleClearAll = () => {
    const cleared = buildDefaultSectionSelection();
    Object.keys(cleared).forEach((id) => {
      cleared[id] = false;
    });
    setSelection(cleared);
  };

  const handleGenerate = async () => {
    if (!investigationId || !hasSelection) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `/api/investigations/${investigationId}/export`,
        { format, sections: selectedIds },
        { responseType: 'blob' },
      );
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await response.data.text();
        throw new Error(text || 'Export failed');
      }
      const blob = new Blob([response.data], {
        type: contentType || 'application/octet-stream',
      });
      const name =
        filenameFromContentDisposition(response.headers['content-disposition'])
        || `argus-export.${format === 'xlsx' ? 'xlsx' : 'csv'}`;
      downloadBlob(blob, name);
      onClose();
    } catch (err) {
      console.error('Investigation export failed', err);
      const message = err?.response?.status === 400
        ? 'Invalid export selection. Choose at least one section.'
        : err?.response?.status === 404
          ? 'Investigation not found.'
          : 'Failed to generate export. Please try again.';
      if (onError) onError(message);
      else window.alert(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PdsModal
      show={isOpen}
      onDismiss={loading ? undefined : onClose}
      minWidth="480px"
      maxWidth="640px"
      minHeight="auto"
      maxHeight="90vh"
    >
      <PdsModalHeader headline="Export investigation data" />
      <PdsModalBody>
        <PdsBox direction="column" gap="md">
          <PdsMantineText type="body" color="neutral">
            Choose tools and sections to export for{targetDomain ? `: ${targetDomain}` : ' this investigation'}.
            All matching records are included (not just the current page).
          </PdsMantineText>

          <PdsBox direction="row" gap="xs" wrap="wrap">
            <PdsMantineButton size="xs" rank="outline" color="neutral" onClick={handleSelectAll} disabled={loading}>
              Select all
            </PdsMantineButton>
            <PdsMantineButton size="xs" rank="outline" color="neutral" onClick={handleClearAll} disabled={loading}>
              Clear all
            </PdsMantineButton>
          </PdsBox>

          <PdsBox direction="column" gap="sm">
            {EXPORT_TOOLS.map((tool) => {
              const state = toolSelectionState(tool, selection);
              return (
                <PdsCard key={tool.id} border borderRadius="sm" padding="sm" direction="column" gap="xs">
                  <PdsBox direction="row" alignItems="center" gap="sm">
                    <PdsCheckbox
                      checked={state.checked}
                      indeterminate={state.indeterminate || undefined}
                      color="action"
                      disabled={loading}
                      onCheckedChange={(checked) => {
                        setSelection((prev) => setToolSections(prev, tool, Boolean(checked)));
                      }}
                    />
                    <PdsMantineText type="textline" fontWeight="strong" component="span">
                      {tool.label}
                    </PdsMantineText>
                  </PdsBox>
                  <PdsBox direction="column" gap="xs" style={{ paddingLeft: 28 }}>
                    {tool.sections.map((section) => (
                      <PdsBox key={section.id} direction="row" alignItems="center" gap="sm">
                        <PdsCheckbox
                          checked={Boolean(selection[section.id])}
                          color="action"
                          disabled={loading}
                          onCheckedChange={(checked) => {
                            setSelection((prev) => ({ ...prev, [section.id]: Boolean(checked) }));
                          }}
                        />
                        <PdsMantineText type="textline" fontSize="300" component="span">
                          {section.label}
                        </PdsMantineText>
                      </PdsBox>
                    ))}
                  </PdsBox>
                </PdsCard>
              );
            })}
          </PdsBox>

          <PdsBox direction="column" gap="xs">
            <PdsMantineText type="textline" fontWeight="strong" component="span">
              Format
            </PdsMantineText>
            <PdsBox direction="row" gap="sm" wrap="wrap">
              <PdsMantineButton
                size="sm"
                rank={format === 'xlsx' ? 'fill' : 'outline'}
                color={format === 'xlsx' ? 'action' : 'neutral'}
                disabled={loading}
                onClick={() => setFormat('xlsx')}
              >
                Excel (.xlsx)
              </PdsMantineButton>
              <PdsMantineButton
                size="sm"
                rank={format === 'csv' ? 'fill' : 'outline'}
                color={format === 'csv' ? 'action' : 'neutral'}
                disabled={loading}
                onClick={() => setFormat('csv')}
              >
                CSV
              </PdsMantineButton>
            </PdsBox>
          </PdsBox>
        </PdsBox>
      </PdsModalBody>
      <PdsModalFooter>
        <PdsBox direction="row" justifyContent="end" gap="sm">
          <PdsMantineButton rank="outline" color="neutral" onClick={onClose} disabled={loading}>
            Cancel
          </PdsMantineButton>
          <PdsMantineButton
            rank="fill"
            color="action"
            onClick={handleGenerate}
            disabled={loading || !hasSelection}
            leadingIcon={loading ? <PdsSpinnerIcon /> : undefined}
          >
            {loading ? 'Generating…' : 'Generate export'}
          </PdsMantineButton>
        </PdsBox>
      </PdsModalFooter>
    </PdsModal>
  );
}
